import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { pedir } from './nucleo'

/**
 * Lo que puede hacer quien mira en **esta cuenta** (plan del CMS dinámico §4): sus
 * capacidades, leídas del núcleo (`GET /api/panel/mis-capacidades`). El panel esconde con
 * esto —una puerta que no abre no se enseña—; el núcleo protege, con las mismas capacidades.
 * El catálogo (`GET /api/panel/capacidades`) es para pintar las casillas al invitar: el
 * panel no lleva su copia (§24).
 */
export type Capacidad = string
export type Preset = 'administrador' | 'autor' | 'editor' | 'marketing' | 'propietario'

export type CatalogoDeCapacidades = {
  grupos: string[]
  capacidades: Array<{ value: Capacidad; label: string; grupo: string }>
  presets: Array<{ value: Preset; etiqueta: string; capacidades: Capacidad[] }>
}

export type MisCapacidades = { cliente: number; capacidades: Capacidad[]; propietario: boolean; equipo: boolean }

/** Lo que las pantallas preguntan: ¿tengo esta capacidad?, ¿soy el dueño de la cuenta?, ¿soy del equipo? */
export type Permisos = {
  cargando: boolean
  capacidades: Set<Capacidad>
  tiene: (capacidad: Capacidad) => boolean
  /** El dueño de la cuenta (acceso de propietario): socios, cobros, crear webs. El equipo cuenta como dueño. */
  esDueno: boolean
  esEquipo: boolean
}

export const SIN_PERMISOS: Permisos = { cargando: true, capacidades: new Set(), tiene: () => false, esDueno: false, esEquipo: false }

export const useCatalogoDeCapacidades = () =>
  useQuery({ queryKey: ['capacidades', 'catalogo'], queryFn: () => pedir<CatalogoDeCapacidades>('/panel/capacidades'), staleTime: Infinity })

export const useMisCapacidades = (espacio: number): Permisos => {
  const { data, isPending } = useQuery({
    queryKey: ['mis-capacidades', espacio],
    queryFn: () => pedir<MisCapacidades>(`/panel/mis-capacidades?cliente=${espacio}`),
    enabled: Number.isInteger(espacio),
    staleTime: 5 * 60_000,
  })
  return useMemo(() => permisosDe(data, isPending), [data, isPending])
}

/** Puro, para las pruebas y el menú: de la respuesta del núcleo a lo que preguntan las pantallas. */
export const permisosDe = (data: MisCapacidades | undefined, cargando = false): Permisos => {
  const capacidades = new Set(data?.capacidades ?? [])
  const esEquipo = data?.equipo ?? false
  return { cargando, capacidades, tiene: (c) => esEquipo || capacidades.has(c), esDueno: data?.propietario ?? false, esEquipo }
}
